import { useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

interface MetricTermProps {
  children: React.ReactNode;
  definition: string;
  calculation?: string;
  interpretation?: string;
  goodRange?: string;
  onOpenAIChat?: () => void;
}

export function MetricTerm({
  children,
  definition,
  calculation,
  interpretation,
  goodRange,
  onOpenAIChat,
}: MetricTermProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="metric-term-button"
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          borderBottom: '1.5px dotted var(--signal-blue)',
          color: 'inherit',
          fontFamily: 'inherit',
          fontSize: 'inherit',
          fontWeight: 'inherit',
          textDecoration: 'none',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderBottomColor = 'var(--accent)';
          e.currentTarget.style.color = 'var(--signal-blue)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderBottomColor = 'var(--signal-blue)';
          e.currentTarget.style.color = 'inherit';
        }}
      >
        {children}
      </button>

      {isOpen && createPortal(
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
          onClick={() => setIsOpen(false)}
        >
          <div
            className="relative rounded-lg p-8 shadow-xl"
            style={{
              backgroundColor: 'var(--card)',
              border: '1px solid var(--border)',
              maxWidth: '560px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setIsOpen(false)}
              className="absolute top-4 right-4 p-2 rounded-lg transition-colors"
              style={{
                color: 'var(--muted-foreground)',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--muted)';
                e.currentTarget.style.color = 'var(--foreground)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = 'var(--muted-foreground)';
              }}
            >
              <X size={20} />
            </button>

            {/* Content */}
            <div className="space-y-6">
              {/* Title */}
              <div>
                <h3 style={{ color: 'var(--foreground)', marginBottom: 'var(--spacing-2)' }}>
                  {children}
                </h3>
                <p style={{ color: 'var(--muted-foreground)', fontSize: 'var(--text-base)' }}>
                  {definition}
                </p>
              </div>

              {/* Calculation */}
              {calculation && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <div
                      className="w-1 h-4 rounded-full"
                      style={{ backgroundColor: 'var(--signal-blue)' }}
                    ></div>
                    <h4 style={{ color: 'var(--foreground)' }}>How it's calculated</h4>
                  </div>
                  <p style={{ color: 'var(--muted-foreground)', fontSize: 'var(--text-sm)' }}>
                    {calculation}
                  </p>
                </div>
              )}

              {/* Interpretation */}
              {interpretation && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <div
                      className="w-1 h-4 rounded-full"
                      style={{ backgroundColor: 'var(--accent)' }}
                    ></div>
                    <h4 style={{ color: 'var(--foreground)' }}>How to interpret this</h4>
                  </div>
                  <p style={{ color: 'var(--muted-foreground)', fontSize: 'var(--text-sm)' }}>
                    {interpretation}
                  </p>
                </div>
              )}

              {/* Good Range */}
              {goodRange && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <div
                      className="w-1 h-4 rounded-full"
                      style={{ backgroundColor: 'var(--alpha-teal)' }}
                    ></div>
                    <h4 style={{ color: 'var(--foreground)' }}>What's a good score?</h4>
                  </div>
                  <p style={{ color: 'var(--muted-foreground)', fontSize: 'var(--text-sm)' }}>
                    {goodRange}
                  </p>
                </div>
              )}

              {/* AI Chat Button */}
              {onOpenAIChat && (
                <button
                  onClick={() => {
                    onOpenAIChat();
                    setIsOpen(false);
                  }}
                  className="w-full rounded-lg px-4 py-3 transition-colors flex items-center justify-center gap-2"
                  style={{
                    backgroundColor: 'var(--accent)',
                    color: 'var(--accent-foreground)',
                    border: 'none',
                    cursor: 'pointer',
                    fontWeight: 'var(--font-weight-medium)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = '0.9';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = '1';
                  }}
                >
                  Continue this conversation with AI →
                </button>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
